import { Session } from 'next-auth';
import { permanentRedirect, redirect } from 'next/navigation';

import { getProductAnimco } from '@services/animco';
import { cmsService } from '@services/cms';
import { getWallConfig } from '@services/config';
import { logger } from '@services/loggers';

import { PalpatineProduct } from '@app-types/api/palpatine';
import { WallType } from '@app-types/config';
import { ProductNotFoundError } from '@app-types/error';
import { ProductParentFull } from '@app-types/product';

import { PAGE_SLUG } from '@helpers/partials';
import { paths } from '@helpers/path';

import { getProductDetails } from '@product-detail/actions';
import { getCurrentProductGencode, shouldRedirectToFilteredWall } from '@product-detail/helpers';

import { ProductDetail } from './product-detail';

interface AccessoriesDetailsProps {
    session?: Session;
    params: { slug: string };
}

export async function AccessoriesDetails({ params }: Readonly<AccessoriesDetailsProps>) {
    let product: ProductParentFull | undefined;

    let productStatics: PalpatineProduct[];

    try {
        [product, { items: productStatics }] = await Promise.all([
            getProductDetails(params.slug, false),
            cmsService.getProduct(),
        ]);
    } catch (error) {
        if (error instanceof ProductNotFoundError) {
            logger.log('🚨 Accessory not found', params.slug);
            const wallConfig = getWallConfig(WallType.ACCESSORY);
            const brandsWithFilter =
                getWallConfig(WallType.ACCESSORY)
                    .filters.find((filter) => filter.key === 'marque')
                    ?.values?.map((brand) => brand.key) ?? [];
            const { shouldRedirect, brand } = shouldRedirectToFilteredWall(params.slug, brandsWithFilter, wallConfig);

            if (shouldRedirect) {
                permanentRedirect(`${paths.accessoryWall}/filtre/${brand}`);
            } else {
                permanentRedirect(paths.accessoryWall);
            }
        }
        redirect(paths.accessoryWall);
    }

    const productGencode: string = getCurrentProductGencode(product, params.slug);
    const currentProduct = product.childs[productGencode]!;

    const animco = getProductAnimco(productStatics, product.urlKey, false);

    return (
        <ProductDetail
            baseUrl={paths.accessoryWall}
            previousPageLabel='Retour vers tous les accessoires'
            displayedBlocks={{
                offersAndDiscounts: false,
                plans: false,
            }}
            product={product}
            currentProduct={currentProduct}
            animco={animco}
            enableKonami
            slug={PAGE_SLUG.ACCESSORY_PRODUCT}
        />
    );
}
